import { useState, type CSSProperties, type FC } from 'react';
import type { TeacherProfile, TeacherScientificProject } from '../types';
import { PlusIcon } from './TeacherIcons';

interface TeacherScientificProjectsOverlayProps {
  projects: TeacherScientificProject[];
  profile?: TeacherProfile | null;
  onBackToCabinet: () => void;
  onSaveWorkspace: () => void;
  onCreateProject: (payload: {
    title: string;
    direction?: string;
    stage_title?: string;
    stage_timeline?: string;
  }) => Promise<void>;
  onAddParticipant: (projectId: string, studentName: string, groupCode: string) => Promise<void>;
  onRemoveParticipant: (projectId: string, studentName: string) => Promise<void>;
}

type SubNav =
  | 'overview'
  | 'theme-roles'
  | 'plan-stages'
  | 'tasks-materials'
  | 'literature'
  | 'documents'
  | 'consultations';

const SUB_NAV: Array<{ id: SubNav; label: string }> = [
  { id: 'overview', label: 'Обзор проекта' },
  { id: 'theme-roles', label: 'Тема и роль' },
  { id: 'plan-stages', label: 'План и этапы' },
  { id: 'tasks-materials', label: 'Задачи и материалы' },
  { id: 'literature', label: 'Литература проекта' },
  { id: 'documents', label: 'Образцы и документы' },
  { id: 'consultations', label: 'Встречи и консультации' },
];

export const TeacherScientificProjectsOverlay: FC<TeacherScientificProjectsOverlayProps> = ({
  projects,
  onBackToCabinet,
  onSaveWorkspace,
  onCreateProject,
  onAddParticipant,
  onRemoveParticipant,
}) => {
  const [selectedProjectId, setSelectedProjectId] = useState<string>(projects[0]?.id || '');
  const [activeSubNav, setActiveSubNav] = useState<SubNav>('overview');
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [isAddParticipantModalOpen, setIsAddParticipantModalOpen] = useState(false);
  const [newProjTitle, setNewProjTitle] = useState('');
  const [newProjDirection, setNewProjDirection] = useState('');
  const [newProjStage, setNewProjStage] = useState('Этап 1 · Вход в проект');
  const [newProjTimeline, setNewProjTimeline] = useState('октябрь 2026 – май 2027');
  const [participantName, setParticipantName] = useState('');
  const [participantGroup, setParticipantGroup] = useState('ҚТӘ-41');

  const activeProject = projects.find((p) => p.id === selectedProjectId) || projects[0];

  const handleCreateProjectSubmit = async () => {
    if (!newProjTitle.trim()) return;
    await onCreateProject({
      title: newProjTitle.trim(),
      direction: newProjDirection.trim() || undefined,
      stage_title: newProjStage,
      stage_timeline: newProjTimeline,
    });
    setNewProjTitle('');
    setNewProjDirection('');
    setIsAddProjectModalOpen(false);
  };

  const handleAddParticipantSubmit = async () => {
    if (!participantName.trim() || !activeProject) return;
    await onAddParticipant(activeProject.id, participantName.trim(), participantGroup);
    setParticipantName('');
    setIsAddParticipantModalOpen(false);
  };

  return (
    <div className="cab-shell cab-shell--wide">
      <div className="cab-top">
        <div className="cab-top-left">
          <div>
            <small>Кабинет преподавателя</small>
            <h2>Научные проекты с обучающимися</h2>
            <p>Создание проекта · участники · роли · этапы · задачи · материалы · консультации · результаты.</p>
          </div>
        </div>
        <div className="cab-top-actions">
          <button type="button" className="cab-btn" onClick={() => setIsAddProjectModalOpen(true)}>
            <PlusIcon className="w-4 h-4" />
            Научный проект
          </button>
          <button type="button" className="cab-btn primary" onClick={onSaveWorkspace}>
            Сохранить
          </button>
          <button type="button" className="cab-btn" onClick={onBackToCabinet}>
            В кабинет
          </button>
        </div>
      </div>

      <div className="cab-body">
        <div className="cabinet-note" style={{ marginBottom: 14 }}>
          Это отдельный научный контур вне дисциплины. Один и тот же объект проекта виден преподавателю и добавленным
          обучающимся.
        </div>

        <div className="cabinet-grid2" style={{ marginBottom: 14 }}>
          {projects.map((p) => (
            <button
              key={p.id}
              type="button"
              className="cabinet-card"
              style={{
                ['--accent' as string]: activeProject?.id === p.id ? '#41d2ff' : '#8faebb',
                textAlign: 'left',
                cursor: 'pointer',
                width: '100%',
                outline: activeProject?.id === p.id ? '1px solid #41d2ff' : undefined,
              }}
              onClick={() => setSelectedProjectId(p.id)}
            >
              <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                {p.stage_title}
              </span>
              <h3 style={{ marginTop: 8 }}>{p.title}</h3>
              <p>
                {p.participants_count || p.participants_json?.length || 0} участн. · {p.progress_percent}%
              </p>
            </button>
          ))}
        </div>

        <div className="cabinet-grid2" style={{ marginBottom: 14 }}>
          <section className="cabinet-panel">
            <div className="cabinet-panel-body">
              <div className="cabinet-actions" style={{ marginBottom: 10, alignItems: 'center' }}>
                <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                  Участники
                </span>
                <strong>{activeProject?.title}</strong>
              </div>
              <div className="cabinet-stack">
                {activeProject?.participants_json && activeProject.participants_json.length > 0 ? (
                  activeProject.participants_json.map((part) => (
                    <article key={part.user_id || part.name} className="cabinet-card" style={{ ['--accent' as string]: '#9e80ff' }}>
                      <div className="cabinet-actions" style={{ alignItems: 'center' }}>
                        <div>
                          <strong>{part.name}</strong>
                          <p style={{ margin: '4px 0 0', fontSize: 12 }}>
                            {part.group} · {part.program}
                            {part.role ? ` · ${part.role}` : ''}
                          </p>
                        </div>
                        <span className="cabinet-spacer" />
                        <button
                          type="button"
                          className="cabinet-btn"
                          onClick={() => onRemoveParticipant(activeProject.id, part.user_id || part.name)}
                        >
                          Убрать
                        </button>
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="cabinet-note">Участники пока не добавлены.</div>
                )}
              </div>
              <div className="cabinet-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="cabinet-btn primary"
                  onClick={() => setIsAddParticipantModalOpen(true)}
                >
                  <PlusIcon className="w-4 h-4" />
                  Добавить обучающегося
                </button>
              </div>
            </div>
          </section>

          <article className="cabinet-card" style={{ ['--accent' as string]: '#e2ad3a' }}>
            <span className="cabinet-badge" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
              Связь кабинетов
            </span>
            <strong style={{ display: 'block', marginTop: 8 }}>Преподаватель ↔ обучающийся</strong>
            <p style={{ margin: '8px 0 0' }}>
              Тема, этапы, задачи, файлы, консультации и результаты являются общими данными проекта.
            </p>
            <div className="cabinet-note" style={{ marginTop: 12 }}>
              Проектный код: <strong>{activeProject?.project_code}</strong>
            </div>
          </article>
        </div>

        <div className="cabinet-filters" style={{ marginBottom: 14 }}>
          {SUB_NAV.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`cabinet-filter${activeSubNav === item.id ? ' active' : ''}`}
              onClick={() => setActiveSubNav(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="cabinet-grid2">
          <section className="cabinet-panel">
            <div className="cabinet-panel-body">
              <span className="cabinet-badge" style={{ '--badge': '#41d2ff' } as CSSProperties}>
                Активный научный проект
              </span>
              <h3 style={{ marginTop: 8 }}>{activeProject?.title}</h3>
              <p>
                {activeProject?.direction || 'Исследовательский проект кафедры с участием студентов.'}
              </p>
              <div className="cabinet-grid2" style={{ marginTop: 12 }}>
                <article className="cabinet-card">
                  <strong>{activeProject?.progress_percent}%</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Прогресс</p>
                </article>
                <article className="cabinet-card">
                  <strong>{activeProject?.next_deadline || '2026-09-30'}</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Ближайший срок</p>
                </article>
                <article className="cabinet-card">
                  <strong>
                    {activeProject?.participants_count || activeProject?.participants_json?.length || 2}
                  </strong>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>Участники</p>
                </article>
                <article className="cabinet-card">
                  <strong>{activeProject?.stage_title}</strong>
                  <p style={{ margin: '4px 0 0', fontSize: 12 }}>
                    {activeProject?.stage_timeline || '2026'}
                  </p>
                </article>
              </div>
            </div>
          </section>

          <section className="cabinet-panel">
            <div className="cabinet-panel-body">
              <strong>Что делать сейчас</strong>
              <div className="cabinet-stack" style={{ marginTop: 10 }}>
                {activeProject?.actions_json && activeProject.actions_json.length > 0 ? (
                  activeProject.actions_json.map((act) => (
                    <article key={act.title} className="cabinet-card" style={{ ['--accent' as string]: '#e2ad3a' }}>
                      <div className="cabinet-actions" style={{ alignItems: 'center' }}>
                        <strong>{act.title}</strong>
                        <span className="cabinet-spacer" />
                        <span className="cabinet-badge" style={{ '--badge': '#e2ad3a' } as CSSProperties}>
                          {act.status}
                        </span>
                      </div>
                      <p style={{ margin: '6px 0 0', fontSize: 12 }}>{act.deadline}</p>
                    </article>
                  ))
                ) : (
                  <div className="cabinet-note">Действий нет.</div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>

      {isAddProjectModalOpen ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal">
            <div className="cabinet-modal-head">
              <div>
                <h2>Создать научный проект</h2>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setIsAddProjectModalOpen(false)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </div>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <label className="cabinet-field">
                  <span>Название проекта</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    placeholder="Student Corpus Lab"
                    value={newProjTitle}
                    onChange={(e) => setNewProjTitle(e.target.value)}
                  />
                </label>
                <label className="cabinet-field">
                  <span>Направление и цели</span>
                  <textarea
                    className="cabinet-textarea"
                    rows={3}
                    placeholder="Сбор, разметка и корпусный анализ..."
                    value={newProjDirection}
                    onChange={(e) => setNewProjDirection(e.target.value)}
                  />
                </label>
                <label className="cabinet-field">
                  <span>Начальный этап</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={newProjStage}
                    onChange={(e) => setNewProjStage(e.target.value)}
                  />
                </label>
                <label className="cabinet-field">
                  <span>Сроки реализации</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={newProjTimeline}
                    onChange={(e) => setNewProjTimeline(e.target.value)}
                  />
                </label>
              </div>
              <div className="cabinet-actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn" onClick={() => setIsAddProjectModalOpen(false)}>
                  Отмена
                </button>
                <button type="button" className="cabinet-btn primary" onClick={handleCreateProjectSubmit}>
                  Создать проект
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {isAddParticipantModalOpen ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal">
            <div className="cabinet-modal-head">
              <div>
                <h2>Добавить обучающегося в проект</h2>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setIsAddParticipantModalOpen(false)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </div>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <label className="cabinet-field">
                  <span>ФИО обучающегося</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    placeholder="Мадина Ермек"
                    value={participantName}
                    onChange={(e) => setParticipantName(e.target.value)}
                  />
                </label>
                <label className="cabinet-field">
                  <span>Группа</span>
                  <input
                    type="text"
                    className="cabinet-input"
                    value={participantGroup}
                    onChange={(e) => setParticipantGroup(e.target.value)}
                  />
                </label>
              </div>
              <div className="cabinet-actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn" onClick={() => setIsAddParticipantModalOpen(false)}>
                  Отмена
                </button>
                <button type="button" className="cabinet-btn primary" onClick={handleAddParticipantSubmit}>
                  Добавить
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
